'use client'

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { useRouter } from 'next/navigation'
import { News } from '@/root/shared-types'
import { HeroNotificationSlide } from './HeroNotificationSlide'
import { ChevronLeftIcon } from '@/root/ui/shared/icons/ChevronLeftIcon'

type Props = {
  news: News[]
  className?: string
  linkTo?: string
  onItemClick?: (item: News) => void
  speedPxPerSec?: number
  direction?: 'left' | 'right'
}

const ITEMS_PER_PAGE = 3
const HERO_NEWS_EVENT = 'HERO_NEWS_SELECT'

export const HeroNotificationsCarousel: FC<Props> = ({
  news,
  className,
  linkTo = '/news',
  onItemClick,
  speedPxPerSec = 22,
  direction = 'left',
}) => {
  const router = useRouter()

  const items = useMemo(() => {
    return (news ?? [])
      .slice()
      .toSorted((a, b) => {
        const db = new Date(b.published_at).getTime()
        const da = new Date(a.published_at).getTime()
        if (db !== da) return db - da
        return Number(b.id) - Number(a.id)
      })
      .slice(0, 12)
  }, [news])

  const hasMany = items.length > 1
  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE)
  const loopItems = useMemo(() => (hasMany ? [...items, ...items] : items), [items, hasMany])
  const itemsWithAlert = useMemo(() => items.map((x) => x?.severity === 'alert'), [items])

  const viewportRef = useRef<HTMLDivElement | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const slideWRef = useRef(0)
  const totalWRef = useRef(0)
  const isFirstMeasureRef = useRef(true)
  const offsetRef = useRef(0)
  const lastTsRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)

  const initialPaddingRef = useRef(0)
  const isPausedRef = useRef(false)
  const [activePage, setActivePage] = useState(0)

  const measure = useCallback(() => {
    const track = trackRef.current
    const vp = viewportRef.current
    if (!track || !vp || items.length === 0) return

    const firstItem = track.children[0] as HTMLElement
    const secondItem = track.children[1] as HTMLElement

    let itemW = firstItem.offsetWidth
    if (secondItem) {
      itemW = secondItem.offsetLeft - firstItem.offsetLeft
    } else {
      itemW += 60
    }

    slideWRef.current = itemW
    totalWRef.current = itemW * items.length

    const vpW = vp.getBoundingClientRect().width
    const calculatedPadding = -vpW / 6 + itemW / 2
    initialPaddingRef.current = calculatedPadding

    if (isFirstMeasureRef.current && totalWRef.current > 0) {
      offsetRef.current = calculatedPadding
      isFirstMeasureRef.current = false
    } else if (totalWRef.current > 0 && offsetRef.current >= 0) {
      offsetRef.current = ((offsetRef.current % totalWRef.current) + totalWRef.current) % totalWRef.current
    }
  }, [items.length])

  useEffect(() => {
    measure()
    const vp = viewportRef.current
    if (!vp) return
    const ro = new ResizeObserver(() => measure())
    ro.observe(vp)
    return () => ro.disconnect()
  }, [measure])

  useEffect(() => {
    if (!hasMany) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const step = (ts: number) => {
      const track = trackRef.current
      if (!track) return

      if (lastTsRef.current == null) lastTsRef.current = ts
      const dt = (ts - lastTsRef.current) / 1000
      lastTsRef.current = ts

      const slideW = slideWRef.current
      const totalW = totalWRef.current

      if (isPausedRef.current || prefersReducedMotion || !slideW || !totalW) {
        rafRef.current = requestAnimationFrame(step)
        return
      }

      const dir = direction === 'left' ? 1 : -1
      offsetRef.current += dir * speedPxPerSec * dt

      if (offsetRef.current >= totalW) offsetRef.current -= totalW
      if (offsetRef.current < -totalW) offsetRef.current += totalW

      track.style.transform = `translateX(${-offsetRef.current}px)`

      const normalizedOffset = (offsetRef.current - initialPaddingRef.current + slideW / 2) % totalW
      const currentItemIndex = Math.floor(normalizedOffset / slideW)
      const currentPage = Math.floor(currentItemIndex / ITEMS_PER_PAGE)
      setActivePage(currentPage % totalPages)

      rafRef.current = requestAnimationFrame(step)
    }

    rafRef.current = requestAnimationFrame(step)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastTsRef.current = null
    }
  }, [hasMany, speedPxPerSec, direction, items.length, totalPages])

  const jumpToPage = (pageIdx: number) => {
    const slideW = slideWRef.current
    const totalW = totalWRef.current
    if (!slideW || !totalW) return

    const targetItemIdx = pageIdx * ITEMS_PER_PAGE
    offsetRef.current = targetItemIdx * slideW + initialPaddingRef.current

    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${-offsetRef.current}px)`
    }
    setActivePage(pageIdx)
  }

  const goPrev = () => jumpToPage((activePage - 1 + totalPages) % totalPages)
  const goNext = () => jumpToPage((activePage + 1) % totalPages)

  const handleHeroClick = (item: News) => {
    if (onItemClick) return onItemClick(item)
    const hasNotifications = !!document.getElementById('notifications')
    if (hasNotifications) {
      const url = new URL(window.location.href)
      url.searchParams.set('newsId', String(item.id))
      window.history.pushState(null, '', url.toString())
      window.dispatchEvent(new CustomEvent(HERO_NEWS_EVENT, { detail: { id: String(item.id) } }))
      return
    }
    router.push(`${linkTo}?newsId=${item.id}`)
  }

  if (!items.length) return null

  return (
    <div className={cn('w-full', className)}>
      <div className="mx-auto flex w-full max-w-295 items-baseline justify-between gap-4 pt-5">
        <button
          type="button"
          onClick={goPrev}
          disabled={!hasMany}
          className="flex h-10 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 disabled:opacity-40 cursor-pointer"
          aria-label="Previous news page"
        >
          <ChevronLeftIcon className="size-6 transition-transform duration-200" />
        </button>

        <div
          ref={viewportRef}
          className="min-w-0 flex-1 overflow-hidden"
          onMouseEnter={() => (isPausedRef.current = true)}
          onMouseLeave={() => (isPausedRef.current = false)}
          onFocus={() => (isPausedRef.current = true)}
          onBlur={() => (isPausedRef.current = false)}
          role="region"
          aria-roledescription="carousel"
          aria-label="Latest news marquee"
        >
          <div ref={trackRef} className="flex items-baseline gap-15 will-change-transform" aria-live="off">
            {loopItems.map((item, idx) => (
              <button
                key={`${idx}-${item.id}`}
                type="button"
                onClick={() => handleHeroClick(item)}
                className="focus-visible:ring-primary-base inline-flex shrink-0 cursor-pointer text-left transition-opacity outline-none hover:opacity-80 focus-visible:ring-2  cursor-pointer"
                aria-label={`News: ${item.subject}`}
              >
                <HeroNotificationSlide item={item} />
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={goNext}
          disabled={!hasMany}
          className="flex h-10 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 disabled:opacity-40  cursor-pointer"
          aria-label="Next news page"
        >
          <ChevronLeftIcon className="size-6 rotate-180 transition-transform duration-200" />
        </button>
      </div>

      {hasMany && (
        <div
          className="mx-auto flex w-full max-w-295 items-center justify-center gap-2 px-4 py-10 sm:px-6 lg:pb-10"
          role="tablist"
        >
          {Array.from({ length: totalPages }).map((_, pageIdx) => {
            const startIdx = pageIdx * ITEMS_PER_PAGE
            const endIdx = startIdx + ITEMS_PER_PAGE
            const pageAlerts = itemsWithAlert.slice(startIdx, endIdx)
            const hasAlert = pageAlerts.some(Boolean)
            const isActive = pageIdx === activePage

            const dotColor = hasAlert
              ? isActive
                ? 'bg-error'
                : 'bg-error/40 hover:bg-error/70'
              : isActive
                ? 'bg-complimentary-dark'
                : 'bg-complimentary-dark/40 hover:bg-complimentary-dark/70'

            return (
              <button
                key={pageIdx}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => jumpToPage(pageIdx)}
                className={cn('size-2 rounded-full transition-all duration-300 cursor-pointer', dotColor, isActive && 'scale-125')}
                aria-label={`Go to news page ${pageIdx + 1}`}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

